import { safe } from "@orpc/client";
import { IconDownload } from "@tabler/icons-react";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import z from "zod";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	FormCard,
	FormCardAction,
	FormCardContent,
	FormCardFooter,
	FormCardHeader,
	FormCardHeaderSubtitle,
	FormCardTitle,
} from "@/components/ui/form-card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { SUPPORTED_AUDIO_TYPES } from "@/constants";
import { ChannelStatus } from "@/enums/channel-status";
import { EpisodeStatus } from "@/enums/episode-status";
import { useAudioUpload } from "@/hooks/use-audio-upload";
import { getCdnUrl } from "@/lib/url";
import { orpc } from "@/orpc/client";

type GuidStrategy = "keep" | "update";

export const Route = createFileRoute("/_app/episodes/$episodeId/audio")({
	component: Page,
	params: z.object({
		episodeId: z.coerce.number(),
	}),
	async loader({ context, params, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.episodes.show.queryOptions({
				input: {
					episodeId: params.episodeId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();
	const [selectedAudio, setSelectedAudio] = useState<File | null>(null);
	const [pendingAudio, setPendingAudio] = useState<File | null>(null);
	const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
	const [guidStrategy, setGuidStrategy] = useState<GuidStrategy>("keep");

	const { data: episode } = useSuspenseQuery(
		orpc.episodes.show.queryOptions({
			input: {
				episodeId: params.episodeId,
			},
		}),
	);

	const updateAudioMutation = useMutation(
		orpc.episodes.updateAudio.mutationOptions({
			onSuccess: async (_data, _payload, _result, context) => {
				await context.client.invalidateQueries({ queryKey: orpc.episodes.key() });
				await context.client.invalidateQueries({
					queryKey: orpc.episodes.show.key({
						input: { episodeId: params.episodeId },
					}),
				});
				toast.success("Episode audio updated successfully.");
			},
			onError: () => {
				toast.error("Failed to update the episode audio.");
			},
		}),
	);

	const uploadAudioMutation = useMutation(
		orpc.episodes.uploadAudio.mutationOptions({
			onError: () => {
				toast.error("Failed to start the audio upload.");
			},
		}),
	);

	const {
		audioInputRef,
		audioUploadError,
		audioUploadProgress,
		isAudioUploading,
		uploadAudioFile,
		setAudioUploadError,
		setAudioUploadProgress,
	} = useAudioUpload({
		selectedFile: selectedAudio,
		requestUploadTarget: async (contentType) => {
			const { data: uploadData, error: uploadError } = await safe(
				uploadAudioMutation.mutateAsync({
					ContentType: contentType,
					ID: episode.ID,
				}),
			);

			if (uploadError || !uploadData) {
				return null;
			}

			return uploadData;
		},
		persistAudio: async (audioKey) => {
			const { error: updateAudioError } = await safe(
				updateAudioMutation.mutateAsync({
					ID: episode.ID,
					AudioKey: audioKey,
					UpdateUID: guidStrategy === "update",
				}),
			);

			return !updateAudioError;
		},
		clearSelectedFile: () => {
			setSelectedAudio(null);
		},
	});

	const isAudioBusy =
		isAudioUploading || uploadAudioMutation.isPending || updateAudioMutation.isPending;

	function handleAudioSelected(file: File | null) {
		setAudioUploadError(null);
		setAudioUploadProgress(null);

		if (!file) {
			setPendingAudio(null);
			return;
		}

		setPendingAudio(file);
		setIsConfirmDialogOpen(true);
	}

	function cancelAudioReplacement() {
		setPendingAudio(null);
		setIsConfirmDialogOpen(false);
		if (audioInputRef.current) {
			audioInputRef.current.value = "";
		}
	}

	function confirmAudioReplacement() {
		if (!pendingAudio) {
			setIsConfirmDialogOpen(false);
			return;
		}

		setSelectedAudio(pendingAudio);
		setIsConfirmDialogOpen(false);
		void uploadAudioFile(pendingAudio);
		setPendingAudio(null);
	}

	const isEpisodeEditable =
		![EpisodeStatus.Removed].includes(episode.StatusID) &&
		![ChannelStatus.Removed, ChannelStatus.Removed_TS].includes(episode.Channel.StatusID);

	return (
		<>
			<div className="grid grid-cols-12 gap-6 px-4 lg:px-6">
				<FormCard className="col-span-12">
					<FormCardHeader>
						<FormCardTitle>Episode Audio</FormCardTitle>
						<FormCardHeaderSubtitle>Listen to the episode directly.</FormCardHeaderSubtitle>
						<FormCardAction>
							<Button
								variant="outline"
								render={<a href={getCdnUrl(episode.Asset?.FilePath)} download />}
							>
								<IconDownload />
								Download Episode Audio
							</Button>
						</FormCardAction>
					</FormCardHeader>
					<FormCardContent>
						<audio controls className="w-full" src={getCdnUrl(episode.Asset?.FilePath)} />
					</FormCardContent>
					<FormCardFooter>
						This audio player uses the episode's original audio file. It won't play the ads or
						jingles.
					</FormCardFooter>
				</FormCard>
				<FormCard className="col-span-12" disabled={!isEpisodeEditable || isAudioBusy}>
					<FormCardHeader>
						<FormCardTitle>Replace Episode Audio</FormCardTitle>
						<FormCardHeaderSubtitle>
							Upload a new audio file for the episode.
						</FormCardHeaderSubtitle>
					</FormCardHeader>
					<FormCardContent>
						<div className="flex flex-col gap-4 rounded-lg border p-4">
							<div>
								<p className="text-sm font-medium">
									{selectedAudio?.name ?? "Upload an MP3, AAC, MP4, OGG or WAV file"}
								</p>
								<p className="text-muted-foreground text-sm">
									Choose a source file and we will replace the current episode audio.
								</p>
							</div>
							<div className="flex flex-wrap items-center gap-3">
								<Button
									type="button"
									variant="outline"
									onClick={() => audioInputRef.current?.click()}
									disabled={isAudioBusy}
								>
									{isAudioBusy ? "Uploading..." : "Choose audio"}
								</Button>
								<input
									ref={audioInputRef}
									type="file"
									accept={Object.keys(SUPPORTED_AUDIO_TYPES).join(",")}
									className="hidden"
									disabled={isAudioBusy}
									onChange={(event) => {
										const nextFile = event.target.files?.[0] ?? null;
										handleAudioSelected(nextFile);
									}}
								/>
								{audioUploadProgress !== null && (
									<span className="text-muted-foreground text-sm">
										{audioUploadProgress}% uploaded
									</span>
								)}
							</div>
							{audioUploadProgress !== null && (
								<div className="bg-muted h-2 overflow-hidden rounded-full">
									<div
										className="bg-primary h-full transition-[width]"
										style={{ width: `${audioUploadProgress}%` }}
									/>
								</div>
							)}
							{audioUploadError && (
								<p className="text-destructive text-sm" role="alert">
									{audioUploadError}
								</p>
							)}
						</div>
					</FormCardContent>
					<FormCardFooter>
						When upload succeeds, the episode is marked for processing and the new source audio is
						used for future playback and delivery.
					</FormCardFooter>
				</FormCard>
			</div>
			<Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
				<DialogContent className="sm:max-w-xl" showCloseButton={false}>
					<DialogHeader>
						<DialogTitle>Confirm Episode Audio Replacement</DialogTitle>
						<DialogDescription>
							Replacing episode audio can temporarily make the episode unavailable while processing
							is running.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						{pendingAudio && (
							<div>
								<p className="text-sm">
									Selected file: <span className="font-medium">{pendingAudio.name}</span>
								</p>
								<audio controls className="mt-2 w-full" src={URL.createObjectURL(pendingAudio)} />
							</div>
						)}
						<div className="space-y-2">
							<p className="text-sm font-medium">Episode GUID behavior</p>
							<RadioGroup
								className="flex flex-col gap-2"
								value={guidStrategy}
								onValueChange={(value) => setGuidStrategy(value as GuidStrategy)}
							>
								<label className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
									<RadioGroupItem value="keep" />
									<span className="text-sm">
										<span className="font-medium">Keep GUID</span>
										<br />
										Keep the current episode identifier. Existing subscribers keep continuity;
										platform behavior may vary on surfacing the update.
									</span>
								</label>
								<label className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
									<RadioGroupItem value="update" />
									<span className="text-sm">
										<span className="font-medium">Update GUID</span>
										<br />
										Generate a new episode identifier to force platforms to treat it as a new
										episode version.
									</span>
								</label>
							</RadioGroup>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={cancelAudioReplacement}>
							Cancel
						</Button>
						<Button onClick={confirmAudioReplacement} disabled={!pendingAudio || isAudioBusy}>
							Confirm & Replace
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
